import { Injectable } from '@angular/core';
import { PrioritizationType } from '@Core/enums/codegen/library.enums';
import { Task } from '@Core/models/codegen/area.models';
import { Prioritization } from '@Core/models/codegen/configuration.models';
import { TenantContext } from '@Core/contexts/tenant-context';
import { TimerType } from '@Core/enums/timer-type.enum';
import { TaskTimer, TaskUtils } from '@Core/utils/task-utils';

import _ from 'lodash';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class TaskTimerService {
    protected ngUnsubscribe: Subject<any> = new Subject();

    private prioritization: Prioritization | null = null;
    private prioritizationOrderMap: PriorityIndexMap | null = null;

    public isInitialized = new BehaviorSubject<boolean>(false);
    public isInitialized$ = this.isInitialized.asObservable();

    constructor(
        private tenantContext: TenantContext
    ) {}

    public initialize(): void {
        this.tenantContext
            .getStore(new Prioritization()).values
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((prioritization) => {
                this.prioritization = prioritization[0]?.data ?? null;

                if (!this.prioritization)
                    return;

                this.prioritizationOrderMap = this.getPrioritizationOrderMap(this.prioritization);

                this.isInitialized.next(true);
        });
    }

    public getMostImminentTimerOnTask(task: Task): TaskTimer | null {
        if (!task)
            return null;

        return TaskUtils.getMostImminentTimerOnTask(task, this.prioritizationOrderMap);
    }

    public getMostImminentTimerInTaskList(tasks: Task[]): TaskTimer | null {
        if (!tasks || !tasks.length)
            return null;

        return TaskUtils.getMostImminentTimerInTaskList(tasks, this.prioritizationOrderMap);
    }

    public getPrioritizationOrderMap(prioritization: Prioritization): PriorityIndexMap {
        if (!prioritization)
            return null;

        return prioritization.PrioritizationTypeOrder
            .reduce((acc, type, index) => {
                acc[type] = index;
                return acc;
            }, {}) as PriorityIndexMap;
    }
    
    public sortTaskTimersByPriority(timers: TaskTimer[]): TaskTimer[] {
        return TaskUtils.sortTaskTimers(timers, this.prioritizationOrderMap);
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }
}

export interface TimeZone {
    id: string;
    name: string;
    description: string;
    offset: number;
    offsetDescription: string;
}

export interface TimerImminentDates {
    [TimerType.Deadline]: Date;
    [TimerType.SLA]: Date;
    [TimerType.Manual]: Date;
}

export type PriorityIndexMap = { [K in PrioritizationType]: number };

export enum DateType {
    Deadline = 'Deadline',
    ServiceLevel = 'ServiceLevel',
    DueDate = 'Manual'
}
